import React, { useState, useRef } from 'react';
import { ShieldIcon, StarIcon, CheckCircle2Icon, MessageCircleIcon, PlayIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { handleCartClick, CART_URL } from '../utils/metaPixel';
interface HeroSectionProps {
  onVideoReady?: () => void;
  showRestOfPage?: boolean;
}
export function HeroSection({
  onVideoReady,
  showRestOfPage = true
}: HeroSectionProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    if (video.duration) {
      const currentProgress = video.currentTime / video.duration * 100;
      setProgress(currentProgress);
      if (currentProgress >= 90 && onVideoReady) {
        onVideoReady();
      }
    }
  };
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  return <section className="pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-b from-dark to-[#1f1f35] overflow-hidden relative" data-id="element-112">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-113">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-rose-900/30 rounded-full blur-3xl" data-id="element-114"></div>
        <div className="absolute top-1/2 -left-24 w-72 h-72 bg-gold-900/20 rounded-full blur-3xl" data-id="element-115"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-116">
        <div className="flex flex-col items-center" data-id="element-117">
          {/* Main Content: Centered */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col items-center space-y-6 text-center max-w-3xl" data-id="element-118">
            <div className="flex items-center space-x-1" data-id="element-119">
              {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className="w-5 h-5 text-gold-500 fill-gold-500" data-id="element-120" />)}
              <span className="ml-2 text-sm font-medium text-gray-400" data-id="element-121">
                Más de 5,000 mujeres felices
              </span>
            </div>

            <h1 className="md:text-5xl lg:text-6xl font-bold text-white text-[32px]" data-id="element-122">
              Deja de escribir como todas <br data-id="element-123" />
              <span className="text-rose-500" data-id="element-124">
                Haz que ÉL vuelva a buscarte sin que lo persigas
              </span>
            </h1>

            <p className="md:text-2xl font-medium text-gray-300 font-['Tiro_Gurmukhi',_serif] text-[20px]" data-id="element-125">
              Domina los mensajes que activan su interés, despiertan su deseo y
              hacen que piense en ti en menos de 2 horas.
            </p>

            <div className="w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden border-2 border-rose-500/20 relative aspect-[9/16] sm:aspect-video bg-black cursor-pointer group" onClick={togglePlay} data-id="element-126">
              <video ref={videoRef} src="https://cdn.magicpatterns.com/uploads/ephcTSyuNf6hyTNNQvYvsQ/0403(2).mp4" className="absolute inset-0 w-full h-full object-contain" onTimeUpdate={handleTimeUpdate} onEnded={() => setIsPlaying(false)} poster="https://cdn.magicpatterns.com/uploads/8XqppsigHBkPiTUhnt1zty/Mockup_principal.optimizado.webp" playsInline muted={isMuted} data-id="element-127" />

              {/* Play Button Overlay */}
              {!isPlaying && <div className="absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity" data-id="element-128">
                  <div className="w-20 h-20 bg-rose-600 rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(225,29,72,0.6)] transform group-hover:scale-110 transition-transform" data-id="element-129">
                    <PlayIcon className="w-10 h-10 text-white ml-2" fill="currentColor" data-id="element-130" />
                  </div>
                </div>}

              {/* Volume Control Button */}
              <button onClick={toggleMute} className="absolute bottom-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors z-20" data-id="element-131">
                {isMuted ? <VolumeXIcon className="w-5 h-5" data-id="element-132" /> : <Volume2Icon className="w-5 h-5" data-id="element-133" />}
              </button>

              {/* Custom Non-interactive Progress Bar */}
              <div className="absolute bottom-0 left-0 w-full h-1.5 bg-gray-800/80" data-id="element-134">
                <div className="h-full bg-rose-500 transition-all duration-300 ease-linear" style={{
                width: `${progress}%`
              }} data-id="element-135" />
              </div>
            </div>

            <p className="text-gray-400 text-[16px] leading-relaxed" data-id="element-136">
              Descubre <strong data-id="element-137">EXACTAMENTE</strong> qué decir en cada momento
              para despertar
              <em data-id="element-138"> deseo, intriga y conexión real</em>…{' '}
              <br className="hidden sm:block" data-id="element-139" />
              <span className="underline decoration-rose-500/50 underline-offset-4" data-id="element-140">
                sin parecer necesitada ni forzada.
              </span>
            </p>

            {showRestOfPage && <>
                <div className="flex flex-wrap justify-center gap-3 py-2" data-id="element-141">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-rose-500/10 text-rose-200 text-sm font-semibold border border-rose-500/30" data-id="element-142">
                    <CheckCircle2Icon className="w-4 h-4 mr-1.5" data-id="element-143" />
                    [Mensajes Probados]
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-rose-500/10 text-rose-200 text-sm font-semibold border border-rose-500/30" data-id="element-144">
                    <MessageCircleIcon className="w-4 h-4 mr-1.5" data-id="element-145" />
                    +100 Frases
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-rose-500/10 text-rose-200 text-sm font-semibold border border-rose-500/30" data-id="element-146">
                    <ShieldIcon className="w-4 h-4 mr-1.5" data-id="element-147" />
                    Método Paso a Paso
                  </span>
                </div>

                <div className="pt-4 flex flex-col items-center space-y-3" data-id="element-148">
                  <a href={CART_URL} onClick={handleCartClick} className="w-full sm:w-auto text-center bg-rose-700 hover:bg-rose-800 text-white font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 animate-pulse-slow text-lg" data-id="element-149">
                    👉 QUIERO QUE ÉL ME BUSQUE AHORA
                  </a>
                  <div className="flex items-center text-sm text-gray-400 font-medium" data-id="element-150">
                    <span className="flex items-center" data-id="element-151">
                      <ShieldIcon className="w-4 h-4 mr-1 text-green-500" data-id="element-152" />{' '}
                      Acceso inmediato
                    </span>
                    <span className="mx-2" data-id="element-153">•</span>
                    <span data-id="element-154">Pago único</span>
                    <span className="mx-2" data-id="element-155">•</span>
                    <span data-id="element-156">Garantía 7 días</span>
                  </div>
                </div>
              </>}
          </motion.div>
        </div>

        {/* Stats Row */}
        {showRestOfPage && <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center border-t border-white/10 pt-8" data-id="element-157">
            <div className="flex flex-col items-center" data-id="element-158">
              <span className="text-4xl font-heading font-bold text-gold-400 mb-2" data-id="element-159">
                +100
              </span>
              <span className="text-gray-400 font-medium uppercase tracking-wider text-sm" data-id="element-160">
                Mensajes Listos pdf
              </span>
            </div>
            <div className="flex flex-col items-center" data-id="element-161">
              <span className="text-4xl font-heading font-bold text-gold-400 mb-2" data-id="element-162">
                3
              </span>
              <span className="text-gray-400 font-medium uppercase tracking-wider text-sm" data-id="element-163">
                Pasos Probados
              </span>
            </div>
            <div className="flex flex-col items-center" data-id="element-164">
              <span className="text-4xl font-heading font-bold text-gold-400 mb-2" data-id="element-165">
                +20 videos
              </span>
              <span className="text-gray-400 font-medium uppercase tracking-wider text-sm" data-id="element-166">
                paso a paso
              </span>
            </div>
          </motion.div>}
      </div>
    </section>;
}